import { useState, useRef, useEffect, KeyboardEvent } from 'react';

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
}

const ChatInput = ({ onSend, disabled = false }: ChatInputProps) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const hasText = value.trim().length > 0;

  return (
    <div className="flex items-center gap-2.5 px-3.5 py-3 border-t border-gray-200 bg-white shrink-0">
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Ask anything about HirePanther…"
        disabled={disabled}
        className="flex-1 h-10 rounded-xl border border-gray-200 px-3.5 text-[14px] text-gray-700 outline-none bg-gray-50 focus:border-gray-300 transition-colors"
        style={{ minWidth: 0 }}
      />
      <button
        onClick={handleSend}
        disabled={!hasText || disabled}
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-opacity"
        style={{
          background: hasText
            ? 'linear-gradient(135deg, var(--chat-accent, #317690), var(--brand-navy, #163544))'
            : '#9CA3AF',
          opacity: hasText && !disabled ? 1 : 0.6,
          cursor: hasText && !disabled ? 'pointer' : 'default',
        }}
        aria-label="Send message"
      >
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 2L11 13" />
          <path d="M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      </button>
    </div>
  );
};

export default ChatInput;
